/*
  「## 読み方のコツ」の節を 1 つの囲みに組み直す (学習パスの本文。MarkdownContent の readingTips を付けたときだけ通す)。
  並びは上から、続けるコツの行 (:::quote)、比べの行 (:::good と :::bad を左右に)、よくある質問の開閉 (::::faq)。

  本文の markdown は書き換えない。節の中の :::good、:::bad、:::quote、::::faq を、
  描き手が決めた札の付く部品 (directive-tips の下の 4 つ) へ移すだけ。
  札は記法の名前で決まるが、この組み直しは「読み方のコツ」の節の中だけで起きる。
  プロンプトの教材の :::good と :::bad (札は「良い指示」と「悪い指示」) は、節の名前が違うので今のまま。
  項目の種類 (task、course、homework) では、読み方の項目とプロンプトの項目を分けられない (本番の DB で両方が task だった)。

  強調は 1 行に 1 種類まで。節の中は用語の点線と語の前のロゴを付けない (data-no-glossary)。
  残すのは書き手が置いた蛍光ペンの帯 (==字==) だけ。問答の答えでは、帯が「速いが揮発する」と「遅いが永続」の対比を担っていて、
  用語の窓は、答えがいま説明している語 (メモリ、ストレージ) を同じ場所でもう一度説明するだけになるため。

  字の決まりはこの節の中の字にだけ当てる (全部の教材に当てるのは別の仕事)。
  全角の括弧は「 (…)」の半角、和文と英数字の間に半角の空きを 1 つ、数と単位 (km など) の間にも空きを 1 つ。
*/

const SECTION_TITLE = "読み方のコツ";
const CJK = "\\u3040-\\u30ff\\u3400-\\u4dbf\\u4e00-\\u9fff";
const UNIT = "(?:km|kg|cm|mm|m|GB|MB|KB|TB|ms|秒|分|時間|日|章|本|行|問|個|回|字|枚|語|件|人|つ)";

/** 節の中の字の決まり。tidyText (apps/client) の題の整え方と同じ規則に、数と単位の空きを足したもの */
export function tidyTipsText(text: string): string {
  return text
    .replace(/\s*（([^（）]*)）\s*/g, " ($1) ")
    .replace(new RegExp(`(\\d)(${UNIT})(?![A-Za-z])`, "g"), "$1 $2")
    .replace(new RegExp(`([${CJK}])([A-Za-z0-9])`, "g"), "$1 $2")
    .replace(new RegExp(`([A-Za-z0-9])([${CJK}])`, "g"), "$1 $2")
    .replace(/ {2,}/g, " ")
    .replace(/ (?=[、。，．」』！？])/g, "");
}

function textOf(node: any): string {
  if (typeof node?.value === "string") return node.value;
  return (node?.children ?? []).map(textOf).join("");
}

function tidyTree(node: any): void {
  if (node?.type === "text" && typeof node.value === "string") {
    node.value = tidyTipsText(node.value);
    return;
  }
  if (node?.type === "inlineCode" || node?.type === "code") return;
  for (const child of node?.children ?? []) tidyTree(child);
}

/** 段落の頭と終わりに整えで付いた空きを落とす (「 (RAM)」で始まる行が空きから始まらないように) */
function trimEdges(paragraphs: any[]): void {
  for (const p of paragraphs) {
    const kids = p?.children ?? [];
    const first = kids[0];
    const last = kids[kids.length - 1];
    if (first?.type === "text") first.value = first.value.replace(/^ +/, "");
    if (last?.type === "text") last.value = last.value.replace(/ +$/, "");
  }
}

/*
  語の途中で折れないように、節の中の字の 3 つの塊を折らない span (md-nowrap) にする。word-break: auto-phrase は効かなかった。
  1 「机の上＝メモリ」のような「＝」でつないだ対 (「机の / 上」と折れた)。両側とも 10 字までで、前後が括弧か区切りの字のときだけ (説明の文を丸ごと折らない塊にしない)
  2 英数字の語と後ろの 1〜2 字の助詞 (「CPU / が」と折れた)
  3 帯の中の 10 字以下の語 (「遅いが / 永続」と帯の中で折れた)
*/
const KEEP = /(?<=^|[「／、。\s])[^\s「」、。／＝]{1,10}＝[^\s「」、。／＝]{1,10}(?=[」／、。\s]|$)|[A-Za-z0-9)][A-Za-z0-9/()]* [ぁ-ん]{1,2}(?=[^ぁ-ん]|$)/g;

function nowrap(value: string): any {
  return { type: "tipsNowrap", data: { hName: "span", hProperties: { className: ["md-nowrap"] } }, children: [{ type: "text", value }] };
}

function keepPhrases(node: any): void {
  const kids: any[] = node?.children;
  if (!Array.isArray(kids)) return;
  const next: any[] = [];
  for (let k = 0; k < kids.length; k++) {
    const c = kids[k];
    const prev = kids[k - 1];
    if (c.type === "text" && prev?.type === "html" && /^<mark\b/.test(prev.value) && c.value.length <= 10) {
      next.push(nowrap(c.value));
      continue;
    }
    if (c.type === "text") {
      let last = 0;
      for (const m of c.value.matchAll(KEEP)) {
        if (m.index! > last) next.push({ type: "text", value: c.value.slice(last, m.index) });
        next.push(nowrap(m[0]));
        last = m.index! + m[0].length;
      }
      if (last < c.value.length) next.push({ type: "text", value: c.value.slice(last) });
      continue;
    }
    if (c.type !== "inlineCode" && c.type !== "code") keepPhrases(c);
    next.push(c);
  }
  node.children = next;
}

function part(name: string, children: any[], props: Record<string, unknown> = {}): any {
  tidyTree({ children });
  trimEdges(children);
  const wrapper = { children };
  keepPhrases(wrapper);
  return {
    type: "tipsPart",
    data: { hName: `directive-tips-${name}`, hProperties: props },
    children: wrapper.children,
  };
}

const isDirective = (node: any, name: string) =>
  node?.type === "containerDirective" && node.name === name;

export function remarkReadingTips() {
  return (tree: any) => {
    const nodes: any[] = tree.children ?? [];
    for (let i = 0; i < nodes.length; i++) {
      const head = nodes[i];
      if (head.type !== "heading" || head.depth !== 2 || textOf(head).trim() !== SECTION_TITLE) continue;
      let end = i + 1;
      while (end < nodes.length && !(nodes[end].type === "heading" && nodes[end].depth <= 2)) end++;
      const section = nodes.slice(i + 1, end);
      const good = section.find((n) => isDirective(n, "good"));
      const bad = section.find((n) => isDirective(n, "bad"));
      // 比べる 2 つが揃っていない節は組み直さない
      if (!good || !bad) continue;
      const quote = section.find((n) => isDirective(n, "quote"));
      const faq = section.find((n) => isDirective(n, "faq"));
      const used = new Set([good, bad, quote, faq].filter(Boolean));
      // 4 つのほかに段落や小見出しがある節、問答の外に字がある節は組み直さない (順番と前置きを崩さない)
      if (section.some((n) => !used.has(n))) continue;
      if (faq && (faq.children ?? []).some((n: any) => !isDirective(n, "faqitem"))) continue;
      const parts: any[] = [];
      if (quote) parts.push(part("lead", quote.children ?? []));
      parts.push({
        type: "tipsPart",
        data: { hName: "directive-tips-compare", hProperties: {} },
        children: [part("good", good.children ?? []), part("bad", bad.children ?? [])],
      });
      if (faq) {
        const items = (faq.children ?? []).filter((n: any) => isDirective(n, "faqitem"));
        const qa = items.map((item: any) => {
          const q = String(item.attributes?.q ?? "").replace(/^Q[.．]\s*/, "");
          return part("qa", item.children ?? [], { q: tidyTipsText(q).trim() });
        });
        if (qa.length) parts.push({ type: "tipsPart", data: { hName: "directive-tips-faq", hProperties: {} }, children: qa });
      }
      const box = {
        type: "readingTips",
        data: {
          hName: "directive-tips",
          hProperties: { dataNoGlossary: "", dataNoIlogo: "" },
        },
        children: parts,
      };
      const rest = section.filter((n) => !used.has(n));
      nodes.splice(i + 1, end - i - 1, box, ...rest);
      i += 1 + rest.length;
    }
  };
}

/*
  :::good と :::bad の札の名前。学習パスの本文 (readingTips を付けたとき) だけ、直前の見出しの種類で替える。
  本文の markdown は書き換えない。札の名前は欄の種類 (kind) から下の表で引く。
  プロンプトの教材 (見出しに「指示」「プロンプト」) は今のまま「良い指示」「悪い指示」。
  どれにも当たらない欄は、AI に頼む型でない中立の「良い例」「悪い例」。
*/
export type ExampleKind = "instruction" | "tips" | "rating" | "example";

export const EXAMPLE_LABELS: Record<ExampleKind, { good: string; bad: string }> = {
  instruction: { good: "良い指示", bad: "悪い指示" },
  tips: { good: "おすすめ", bad: "つまずきやすい所" },
  rating: { good: "評価される", bad: "評価されにくい" },
  example: { good: "良い例", bad: "悪い例" },
};

const INSTRUCTION_HEADING = /指示|プロンプト/;
const TIPS_HEADING = /コツ|ヒント|心がけ|心構え|マインドセット|読み方|学び方|進め方|取り組み方|向き合い方|つまずき/;
const RATING_HEADING = /評価ポイント/;

export function exampleKindOf(heading: string): ExampleKind {
  if (INSTRUCTION_HEADING.test(heading)) return "instruction";
  if (RATING_HEADING.test(heading)) return "rating";
  if (TIPS_HEADING.test(heading)) return "tips";
  return "example";
}

/** 直前の見出し (2 から 4 段) の種類を :::good と :::bad の kind に付ける。readingTips のときだけ通す */
export function remarkExampleKinds() {
  return (tree: any) => {
    let heading = "";
    const walk = (nodes: any[]) => {
      for (const node of nodes) {
        if (node.type === "heading" && node.depth >= 2 && node.depth <= 4) {
          heading = textOf(node).trim();
        } else if (isDirective(node, "good") || isDirective(node, "bad")) {
          const data = (node.data ??= {});
          data.hProperties = { ...(data.hProperties ?? {}), kind: exampleKindOf(heading) };
        } else if (node.type === "containerDirective" && Array.isArray(node.children)) {
          walk(node.children);
        }
      }
    };
    walk(tree.children ?? []);
  };
}
